import {
  ComboboxMulti,
  ComboboxMultiButtonTrigger,
  ComboboxMultiContent,
  ComboboxMultiEmpty,
  ComboboxMultiInlineContent,
  ComboboxMultiInput,
  ComboboxMultiItem,
  ComboboxMultiList,
  ComboboxMultiSearchInput,
  ComboboxMultiStackedInput,
  DotSpinner,
} from '@crono-one/design-system';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { ReactNode, useEffect, useState } from 'react';
import { cn } from 'utils/style-utils';
import { SEARCH_EDIT_REASONS, useLoadMoreSentinel } from './shared';

export interface InfiniteComboboxMultiProps<T> {
  id?: string;
  values: T[];
  onChange: (values: T[]) => void;
  options: T[];
  getOptionKey: (option: T) => string;
  getOptionLabel: (option: T) => string;
  renderOption?: (option: T) => ReactNode;
  // Chip content for the selected values in the 'stacked' variant. Defaults to
  // getOptionLabel — required for object values, since the DS default only
  // understands {label}/{value} objects and JSON.stringifies anything else.
  renderChip?: (option: T) => ReactNode;
  // Replaces the whole chip rather than its label, for a chip that carries its
  // own colour instead of the default one.
  renderChipItem?: (option: T) => ReactNode;
  // Fired with the debounced input text.
  onSearchChange: (search: string) => void;
  onOpenChange?: (open: boolean) => void;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  empty?: ReactNode;
  loadingLabel?: string;
  loadingMoreLabel?: string;
  loading?: ReactNode;
  loadingMore?: ReactNode;
  debounceMs?: number;
  disabled?: boolean;
  dsSize?: 'sm' | 'md';
  // 'trigger' (default): a button anchor with the search input inside the popup.
  // 'stacked': the anchor is an input with the chips stacked above it.
  // 'inline': the anchor is an input with the chips inside it.
  // Both input variants drive the search from their own field.
  variant?: 'trigger' | 'stacked' | 'inline';
  // 'popup' (default): the list floats over the page, anchored to the input.
  // 'in-flow': the list opens below the input and takes up room there, so it
  // scrolls with whatever holds it instead of hanging over it.
  listPlacement?: 'popup' | 'in-flow';
  chipsLayout?: 'wrap' | 'column';
  triggerClassName?: string;
  positionerClassname?: string;
  triggerChildren?: React.ReactNode;
  // A combobox takes its accessible name from a label, never from its content,
  // so an anchor with no visible <label> needs this to be reachable by name.
  triggerAriaLabel?: string;
}

// Server-paginated + infinite-scroll ComboboxMulti with debounced
// server-search. Multi-select sibling of InfiniteCombobox, rendered as a
// button trigger with the search input inside the popup.
const InfiniteComboboxMulti = <T,>({
  id,
  values,
  onChange,
  options,
  getOptionKey,
  getOptionLabel,
  renderOption,
  renderChip,
  renderChipItem,
  onSearchChange,
  onOpenChange,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  empty = 'No results',
  loadingLabel = 'Loading...',
  loadingMoreLabel = loadingLabel,
  loading = <Loading label={loadingLabel} />,
  loadingMore = <Loading label={loadingMoreLabel} className="py-2 text-sm" />,
  debounceMs = 500,
  disabled,
  dsSize,
  variant = 'trigger',
  listPlacement = 'popup',
  chipsLayout,
  triggerClassName,
  positionerClassname,
  triggerChildren,
  triggerAriaLabel,
}: InfiniteComboboxMultiProps<T>) => {
  const [inputValue, setInputValue] = useState('');
  const debouncedSearch = useDebouncedValue(inputValue, debounceMs);

  useEffect(() => {
    onSearchChange(debouncedSearch);
  }, [debouncedSearch, onSearchChange]);

  const setSentinel = useLoadMoreSentinel({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <ComboboxMulti
      items={options}
      value={values}
      onValueChange={onChange}
      onOpenChange={onOpenChange}
      onInputValueChange={(next, details) => {
        if (SEARCH_EDIT_REASONS.includes(details.reason)) {
          setInputValue(next);
        }
      }}
      itemToStringLabel={getOptionLabel}
      isItemEqualToValue={(a: T, b: T) => getOptionKey(a) === getOptionKey(b)}
      filter={() => true}
      disabled={disabled}
    >
      {variant === 'stacked' && (
        <ComboboxMultiStackedInput
          id={id}
          placeholder={placeholder}
          dsSize={dsSize}
          chipsLayout={chipsLayout}
          showClear
          renderChip={renderChip ?? getOptionLabel}
          renderChipItem={renderChipItem}
          className={triggerClassName}
          aria-label={triggerAriaLabel}
        />
      )}
      {variant === 'inline' && (
        <ComboboxMultiInput
          id={id}
          placeholder={placeholder}
          dsSize={dsSize}
          showClear
          renderChip={renderChip ?? getOptionLabel}
          renderChipItem={renderChipItem}
          className={triggerClassName}
          aria-label={triggerAriaLabel}
        />
      )}
      {variant === 'trigger' && (
        <ComboboxMultiButtonTrigger
          placeholder={placeholder}
          size={dsSize}
          className={triggerClassName}
          aria-label={triggerAriaLabel}
          children={triggerChildren}
        />
      )}
      <List
        listPlacement={listPlacement}
        positionerClassname={positionerClassname}
      >
        {variant === 'trigger' && (
          <ComboboxMultiSearchInput id={id} placeholder={searchPlaceholder} />
        )}
        <ComboboxMultiEmpty>{isLoading ? loading : empty}</ComboboxMultiEmpty>
        <ComboboxMultiList>
          {options.map((item: T) => (
            <ComboboxMultiItem key={getOptionKey(item)} value={item}>
              {renderOption ? renderOption(item) : getOptionLabel(item)}
            </ComboboxMultiItem>
          ))}
          {hasNextPage && <div ref={setSentinel} className="h-px" />}
        </ComboboxMultiList>
        {isFetchingNextPage && loadingMore}
      </List>
    </ComboboxMulti>
  );
};

interface ListProps {
  listPlacement: 'popup' | 'in-flow';
  positionerClassname?: string;
  children: ReactNode;
}

// In-flow content has no positioner to aim, so the popup class has nowhere to go.
const List = ({ listPlacement, positionerClassname, children }: ListProps) =>
  listPlacement === 'in-flow' ? (
    <ComboboxMultiInlineContent>{children}</ComboboxMultiInlineContent>
  ) : (
    <ComboboxMultiContent positionerClassname={positionerClassname}>
      {children}
    </ComboboxMultiContent>
  );

interface LoadingProps {
  label: string;
  className?: string;
}

const Loading = ({ label, className }: LoadingProps) => (
  <div
    className={cn(
      'flex justify-center items-center gap-2 text-sky-700',
      className,
    )}
  >
    {label} <DotSpinner className="size-5" />
  </div>
);

export default InfiniteComboboxMulti;
